import React, { useEffect, useState, useRef, useMemo } from 'react';
import {
    Box, Typography, TextField, Avatar, List, ListItemButton, ListItemAvatar, InputAdornment, IconButton,
    Badge, Stack, Menu, MenuItem, Tooltip, Divider, Dialog, DialogTitle, DialogContent, DialogActions, Button, Chip
} from '@mui/material';
import { useSelector } from 'react-redux';
import axios from 'axios';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import SearchIcon from '@mui/icons-material/Search';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import MessageInput from './MessageInput';
import GroupIcon from '@mui/icons-material/Group';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import Picker from '@emoji-mart/react';
import data from '@emoji-mart/data';
import emoji from 'emoji-dictionary';
import AvatarGroup from '@mui/material/AvatarGroup';
import { useOutletContext, useParams } from 'react-router-dom';

const asciiEmojiMap = {
    ':)': '😊', ':-)': '😊', ':(': '😞', ':-(': '😞', ':D': '😃', ':-D': '😃',
    ':P': '😛', ':-P': '😛', ';-)': '😉', ';)': '😉', ':/': '😕', ":'(": '😢',
    ':O': '😮', '<3': '❤️', ':*': '😘', ':|': '😐', 'XD': '😆', 'xD': '😆'
};

const QUICK_EMOJIS = ['👍', '😂', '❤️', '😊'];

function escapeRegExp(string) { return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); }
function replaceAsciiEmojis(text) {
    let result = text;
    Object.keys(asciiEmojiMap).forEach(key => {
        const regex = new RegExp(escapeRegExp(key), 'g');
        result = result.replace(regex, asciiEmojiMap[key]);
    });
    return result;
}
function replaceShortcodeEmojis(text) { return text.replace(/:([\w+-]+):/g, m => emoji.getUnicode(m) || m); }
function convertEmojis(text) { return replaceAsciiEmojis(replaceShortcodeEmojis(text)); }

const statusOptions = [
    { value: 'available', label: 'Available', color: '#4caf50' },
    { value: 'busy', label: 'Busy', color: '#ff9800' },
    { value: 'away', label: 'Away', color: '#bdbdbd' },
    { value: 'do-not-disturb', label: 'Do Not Disturb', color: '#d32f2f' },
];

function groupReactions(reactions, userMap = {}) {
    if (!Array.isArray(reactions)) return [];
    const map = {};
    reactions.forEach(r => {
        const name =
            (userMap && userMap[r.userId]) || r.userName || r.name || r.userId;
        if (!map[r.emoji]) map[r.emoji] = [];
        if (!map[r.emoji].includes(name)) map[r.emoji].push(name);
    });
    return Object.entries(map).map(([emoji, users]) => ({ emoji, users }));
}



function StatusDot({ isOnline, status, sx = {} }) {
    let color = '#bdbdbd';
    if (isOnline) {
        const opt = statusOptions.find(o => o.value === status) || statusOptions[0];
        color = opt.color;
    }
    return <FiberManualRecordIcon fontSize="small" sx={{ color, mr: 1, ...sx }} />;
}
function getStatusLabel(isOnline, status) {
    if (!isOnline) return 'Offline';
    const opt = statusOptions.find(o => o.value === status) || statusOptions[0];
    return `Online (${opt.label})`;
}
function formatChatDate(dateStr) {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    const dY = d.toISOString().slice(0, 10);
    const tY = today.toISOString().slice(0, 10);
    const yY = yesterday.toISOString().slice(0, 10);
    if (dY === tY) return 'Today';
    if (dY === yY) return 'Yesterday';
    return d.toLocaleDateString();
}
function formatTime(dateStr) {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}


const ChatPage = ({ socket }) => {
    const token = useSelector(s => s.auth.token);
    const [contacts, setContacts] = useState([]);
    const [contactsMessages, setContactsMessages] = useState({});
    const [contactsUnread, setContactsUnread] = useState({});
    const [groups, setGroups] = useState([]);
    const [groupMessages, setGroupMessages] = useState({});
    const [selected, setSelected] = useState(null); // {type: 'user'|'group', ...}
    const [messages, setMessages] = useState([]);
    const [userStatuses, setUserStatuses] = useState({});
    const [newMessage, setNewMessage] = useState('');
    const [search, setSearch] = useState('');
    const [profileData, setProfileData] = useState({});
    const [statusAnchorEl, setStatusAnchorEl] = useState(null);
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const emojiPickerRef = useRef();
    const chatBoxRef = useRef(null);
    const [deleteGroupDialogOpen, setDeleteGroupDialogOpen] = useState(false);
    const [typingUsers, setTypingUsers] = useState({}); // {teamId: [users]}
    const { userId: userIdParam } = useParams();
    const { refreshChatUnread } = useOutletContext() || {};




    const [hoveredMessageId, setHoveredMessageId] = useState(null);
    const [reactionPicker, setReactionPicker] = useState({ messageId: null, anchorEl: null });



    // Track typing state/timer
    const [isTyping, setIsTyping] = useState(false);
    const typingTimeoutRef = useRef();

    // For group creation
    const [groupDialogOpen, setGroupDialogOpen] = useState(false);
    const [groupName, setGroupName] = useState('');
    const [groupMembers, setGroupMembers] = useState([]);

    // For group menu (leave/delete/add members)
    const [groupMenuAnchorEl, setGroupMenuAnchorEl] = useState(null);
    const [addMemberDialogOpen, setAddMemberDialogOpen] = useState(false);
    const [newGroupMembers, setNewGroupMembers] = useState([]);

    let currentUserId = null;
    if (token) {
        try { currentUserId = JSON.parse(atob(token.split('.')[1])).id; } catch { }
    }
    const avatarUrl = u =>
        u?.avatarUrl ? `/api/users/avatars/${u._id}/${u.avatarUrl.split('/').pop()}` : '';

    // --- Fetch current user profile
    useEffect(() => {
        if (!token) return;
        axios.get('/api/users/me', { headers: { Authorization: `Bearer ${token}` } })
            .then(r => setProfileData(r.data))
            .catch(console.error);
    }, [token]);

    useEffect(() => {
        if (userIdParam && contacts.length > 0) {
            // Find the contact with this userId
            const contact = contacts.find(c => c.user._id === userIdParam);
            if (contact) {
                setSelected({ ...contact, type: 'user' });
            }
        }
    }, [userIdParam, contacts]);


    // --- Fetch contacts (same as before)
    useEffect(() => {
        if (!token) return;
        (async () => {
            try {
                const { data } = await axios.get('/api/team/member-teams', {
                    headers: { Authorization: `Bearer ${token}` },
                });
                const map = {};
                data.forEach(team => {
                    if (team.owner?._id && team.owner._id !== currentUserId) {
                        map[team.owner._id] = { teamId: team._id, user: team.owner };
                    }
                    team.translators.forEach(t => {
                        const u = t.userId || t;
                        if (u._id !== currentUserId) {
                            map[u._id] = { teamId: team._id, user: u };
                        }
                    });
                });
                setContacts(Object.values(map));
                // NEW: Join all teams the user is a member of
                if (socket) {
                    const allTeamIds = Array.from(new Set(Object.values(map).map(c => c.teamId)));
                    allTeamIds.forEach(teamId => {
                        socket.emit('joinTeam', teamId);
                    });
                }
            } catch (e) { console.error(e); }
        })();
    }, [token, currentUserId, socket]);

    useEffect(() => {
        if (!socket) return;

        const onTyping = ({ teamId, user }) => {
            setTypingUsers(prev => {
                const arr = (prev[teamId] || []).filter(u => u._id !== user._id);
                return { ...prev, [teamId]: [...arr, user] };
            });
        };
        const onStopTyping = ({ teamId, user }) => {
            setTypingUsers(prev => {
                const arr = (prev[teamId] || []).filter(u => u._id !== user._id);
                return { ...prev, [teamId]: arr };
            });
        };

        socket.on('userTyping', onTyping);
        socket.on('userStopTyping', onStopTyping);

        return () => {
            socket.off('userTyping', onTyping);
            socket.off('userStopTyping', onStopTyping);
        };
    }, [socket]);


    // --- Fetch group chats the user is a member of
    useEffect(() => {
        if (!token) return;
        axios.get('/api/group-chat', { headers: { Authorization: `Bearer ${token}` } })
            .then(res => setGroups(Array.isArray(res.data) ? res.data : []))
            .catch(console.error);
    }, [token]);

    // --- Fetch preview & unread for contacts (same as before)
    useEffect(() => {
        if (!token || !contacts.length) return;
        (async () => {
            const lastMsgObj = {};
            const unreadObj = {};
            await Promise.all(
                contacts.map(async contact => {
                    let contactMsg = null;
                    try {
                        const res = await axios.get(
                            `/api/team-chat/${contact.teamId}/${contact.user._id}`,
                            { headers: { Authorization: `Bearer ${token}` }, params: { limit: 50 } }
                        );
                        const msgs = res.data || [];
                        contactMsg = msgs.length ? msgs[msgs.length - 1] : null;
                    } catch { contactMsg = null; }
                    lastMsgObj[contact.user._id] = contactMsg;
                    unreadObj[contact.user._id] = 0;
                })
            );
            setContactsMessages(lastMsgObj);
            setContactsUnread(unreadObj);
        })();
    }, [contacts, token]);

    // --- Fetch group chat previews (last message)
    useEffect(() => {
        if (!token || !groups.length) return;
        (async () => {
            const lastMsgObj = {};
            await Promise.all(
                groups.map(async group => {
                    let lastMsg = null;
                    try {
                        const res = await axios.get(
                            `/api/group-chat/${group._id}/messages`,
                            { headers: { Authorization: `Bearer ${token}` }, params: { limit: 1 } }
                        );
                        const msgs = res.data || [];
                        lastMsg = msgs.length ? msgs[msgs.length - 1] : null;
                    } catch { }
                    lastMsgObj[group._id] = lastMsg;
                })
            );
            setGroupMessages(lastMsgObj);
        })();
    }, [groups, token]);

    // --- Fetch statuses (same as before)
    useEffect(() => {
        if (!token) return;
        axios.get('/api/users/status', { headers: { Authorization: `Bearer ${token}` } })
            .then(r => setUserStatuses(r.data))
            .catch(console.error);
    }, [token]);

    // --- Live status updates
    useEffect(() => {
        if (!socket) return;
        const h = ({ userId, isOnline, status }) => {
            setUserStatuses(u => ({ ...u, [userId]: { isOnline, status } }));
            if (userId === profileData._id) setProfileData(d => ({ ...d, status }));
        };
        socket.on('statusUpdate', h);
        return () => socket.off('statusUpdate', h);
    }, [socket, profileData._id]);

    // --- Join group or team room on selection
    useEffect(() => {
        if (!socket || !selected) return;
        if (selected.type === 'group') socket.emit('joinGroup', selected._id);
        if (selected.type === 'user') socket.emit('joinTeam', selected.teamId);
    }, [socket, selected]);

    // --- Fetch chat history & clear unread (for user)
    useEffect(() => {
        if (!selected || selected.type !== 'user') return;
        (async () => {
            try {
                const { data } = await axios.get(
                    `/api/team-chat/${selected.teamId}/${selected.user._id}`,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                setMessages(data);
                await axios.put(
                    `/api/team-chat/${selected.teamId}/${selected.user._id}/read`,
                    {},
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                setContactsUnread(u => ({ ...u, [selected.user._id]: 0 }));
                refreshChatUnread?.();
                setTimeout(
                    () => chatBoxRef.current?.scrollTo(0, chatBoxRef.current.scrollHeight),
                    100
                );
            } catch (e) { console.error(e); }
        })();
    }, [selected, token, refreshChatUnread]);

    // --- Fetch group chat history
    useEffect(() => {
        if (!selected || selected.type !== 'group') return;
        (async () => {
            try {
                // 1. Fetch messages
                const { data } = await axios.get(
                    `/api/group-chat/${selected._id}/messages`,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                setMessages(data);

                // 2. Mark all as read on backend
                await axios.put(`/api/group-chat/${selected._id}/read`, {}, {
                    headers: { Authorization: `Bearer ${token}` }
                });

                // 3. Locally set unread to 0 for this group in your list!
                setGroups(groups =>
                    groups.map(g =>
                        g._id === selected._id ? { ...g, unread: 0 } : g
                    )
                );

                refreshChatUnread?.();

                setTimeout(
                    () => chatBoxRef.current?.scrollTo(0, chatBoxRef.current.scrollHeight),
                    100
                );
            } catch (e) { console.error(e); }
        })();
    }, [selected, token, refreshChatUnread]);






    // --- Live incoming messages (user and group)
    useEffect(() => {
        if (!socket) return;
        const hTeam = msg => {
            const fromId =
                msg.senderId._id === currentUserId
                    ? msg.receiverId._id
                    : msg.senderId._id;
            setContactsMessages(m => ({ ...m, [fromId]: msg }));
            // Only increment unread if the message is not part of the currently open chat
            if (!(selected && selected.type === 'user' && (
                (msg.senderId._id === currentUserId && msg.receiverId._id === selected.user._id) ||
                (msg.senderId._id === selected.user._id && msg.receiverId._id === currentUserId)
            ))) {
                setContactsUnread(u => ({
                    ...u,
                    [fromId]: (u[fromId] || 0) + 1,
                }));
            }

            if (
                selected &&
                selected.type === 'user' &&
                ((msg.senderId._id === selected.user._id &&
                    msg.receiverId._id === currentUserId) ||
                    (msg.senderId._id === currentUserId &&
                        msg.receiverId._id === selected.user._id))
            ) {
                setMessages(prev => [...prev, msg]);
            }
        };
        const hGroup = msg => {
            if (selected && selected.type === 'group' && selected._id === msg.groupId) {
                setMessages(prev => [...prev, msg]);
                // Do NOT increment unread
            } else {
                // Increment unread for the correct group
                setGroups(groups => groups.map(g =>
                    g._id === msg.groupId ? { ...g, unread: (g.unread || 0) + 1 } : g
                ));
            }
            setGroupMessages(m => ({ ...m, [msg.groupId]: msg }));
        };

        socket.on('receiveTeamMessage', hTeam);
        socket.on('receiveGroupMessage', hGroup);
        return () => {
            socket.off('receiveTeamMessage', hTeam);
            socket.off('receiveGroupMessage', hGroup);
        };
    }, [socket, selected, currentUserId]);

    // --- Listen for emoji reaction events ---
    // --- Listen for emoji reaction events (team and group) ---
    useEffect(() => {
        if (!socket) return;

        // Handler for team (1:1) chat reactions: receives the full updated message from the server
        const handleTeamReaction = updatedMsg => {
            setMessages(msgs =>
                msgs.map(msg =>
                    msg._id === updatedMsg._id ? updatedMsg : msg
                )
            );
        };

        // Handler for group chat reactions: receives the full updated message from the server
        const handleGroupReaction = updatedMsg => {
            setMessages(msgs =>
                msgs.map(msg =>
                    msg._id === updatedMsg._id ? updatedMsg : msg
                )
            );
        };

        socket.on('teamMessageReaction', handleTeamReaction);
        socket.on('groupMessageReaction', handleGroupReaction);

        return () => {
            socket.off('teamMessageReaction', handleTeamReaction);
            socket.off('groupMessageReaction', handleGroupReaction);
        };
    }, [socket]);

    useEffect(() => {
        if (chatBoxRef.current) {
            chatBoxRef.current.scrollTo(0, chatBoxRef.current.scrollHeight);
        }
    }, [messages]);



    // --- Emoji picker
    useEffect(() => {
        const h = e => {
            if (
                showEmojiPicker &&
                emojiPickerRef.current &&
                !emojiPickerRef.current.contains(e.target)
            ) {
                setShowEmojiPicker(false);
            }
        };
        document.addEventListener('mousedown', h);
        return () => document.removeEventListener('mousedown', h);
    }, [showEmojiPicker]);

    // --- Group menu handlers
    const isGroupOwner =
        selected?.type === 'group' &&
        (
            (typeof selected.createdBy === 'object' && selected.createdBy?._id === currentUserId) ||
            (typeof selected.createdBy === 'string' && selected.createdBy === currentUserId)
        );

    const handleGroupMenuOpen = (e) => setGroupMenuAnchorEl(e.currentTarget);
    const handleGroupMenuClose = () => setGroupMenuAnchorEl(null);

    const handleDeleteGroup = async () => {
        try {
            await axios.delete(`/api/group-chat/${selected._id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setGroups(groups => groups.filter(g => g._id !== selected._id));
            setSelected(null);
        } catch (err) {
            alert('Failed to delete group.');
        }
        handleGroupMenuClose();
    };

    function handleTyping() {
        if (!selected || selected.type !== 'user') return;
        if (!isTyping) {
            setIsTyping(true);
            socket.emit('typing', { teamId: selected.teamId, user: profileData });
        }
        clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = setTimeout(() => {
            setIsTyping(false);
            socket.emit('stopTyping', { teamId: selected.teamId, user: profileData });
        }, 1500); // 1.5 seconds after last keystroke
    }

    const handleLeaveGroup = async () => {
        try {
            await axios.put(`/api/group-chat/${selected._id}/leave`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setGroups(groups => groups.filter(g => g._id !== selected._id));
            setSelected(null);
        } catch (err) {
            alert('Failed to leave group.');
        }
        handleGroupMenuClose();
    };

    // --- Add member dialog handlers
    const handleOpenAddMembers = () => {
        setAddMemberDialogOpen(true);
        setNewGroupMembers([]);
        handleGroupMenuClose();
    };
    const handleAddMembersToGroup = async () => {
        try {
            await axios.post(
                `/api/group-chat/${selected._id}/add-members`,
                { members: newGroupMembers },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            // Fetch updated group
            const { data } = await axios.get(`/api/group-chat/${selected._id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setGroups(groups => groups.map(g => g._id === data._id ? data : g));
            setSelected(sel => ({ ...sel, members: data.members }));
            setAddMemberDialogOpen(false);
        } catch (err) {
            alert('Failed to add members');
        }
    };

    const handleAddEmoji = obj => {
        setNewMessage(m => m + (obj.native || obj.emoji));
        setShowEmojiPicker(false);
    };
    const sendMessage = async () => {
        if (!newMessage.trim() || !selected || !socket) return;
        if (selected.type === 'user') {
            const msg = {
                teamId: selected.teamId,
                senderId: {
                    _id: currentUserId,
                    firstName: profileData.firstName,
                    lastName: profileData.lastName,
                    avatarUrl: profileData.avatarUrl,
                },
                receiverId: selected.user,
                message: convertEmojis(newMessage),
                timestamp: new Date().toISOString(),
            };
            socket.emit('sendTeamMessage', {
                ...msg,
                senderId: currentUserId,      // Only IDs for server
                receiverId: selected.user._id // Only IDs for server
            });
        } else if (selected.type === 'group') {
            const msg = {
                groupId: selected._id,
                senderId: currentUserId,
                message: convertEmojis(newMessage),
                timestamp: new Date().toISOString(),
            };
            socket.emit('sendGroupMessage', msg);
            setGroupMessages(m => ({ ...m, [selected._id]: msg }));

            // --- THIS IS THE KEY BIT: Mark as read right after sending your own message
            try {
                await axios.put(
                    `/api/group-chat/${selected._id}/read`,
                    {},
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                setGroups(groups =>
                    groups.map(g =>
                        g._id === selected._id ? { ...g, unread: 0 } : g
                    )
                );
            } catch (err) {
                console.warn('Failed to mark group as read after sending message', err);
            }
        }
        setNewMessage('');
    };


    // --- Group creation
    const handleOpenGroupDialog = () => {
        setGroupDialogOpen(true);
        setGroupName('');
        setGroupMembers([]);
    };
    const handleCreateGroup = async () => {
        if (!groupName.trim() || groupMembers.length < 1) return;
        try {
            const res = await axios.post(
                '/api/group-chat',
                {
                    name: groupName,
                    members: Array.from(new Set([...groupMembers, currentUserId])) // ensures no duplicates
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setGroups(g => [...g, res.data]);
            setGroupDialogOpen(false);
        } catch (err) { alert('Failed to create group.'); }
    };

    // --- Filtering (defensively handle missing arrays)
    const filteredContacts = useMemo(() => {
        return Array.isArray(contacts)
            ? contacts
                .filter(c =>
                    `${c.user.firstName} ${c.user.lastName}`.toLowerCase().includes(search.toLowerCase())
                )
                .sort((a, b) => {
                    const aMsg = contactsMessages[a.user._id];
                    const bMsg = contactsMessages[b.user._id];
                    // If both have a last message, compare timestamps
                    if (aMsg && bMsg) {
                        return new Date(bMsg.timestamp) - new Date(aMsg.timestamp);
                    }
                    // If only a has message, a comes first
                    if (aMsg) return -1;
                    // If only b has message, b comes first
                    if (bMsg) return 1;
                    // Otherwise, fallback to name sort
                    return (
                        `${a.user.firstName} ${a.user.lastName}`.localeCompare(
                            `${b.user.firstName} ${b.user.lastName}`
                        )
                    );
                })
            : [];
    }, [contacts, search, contactsMessages]);

    const filteredGroups = useMemo(() => {
        return Array.isArray(groups) ? groups.filter(g =>
            typeof g.name === 'string' && g.name.toLowerCase().includes(search.toLowerCase())
        ) : [];
    }, [groups, search]);

    // --- Utility
    function groupByDate(arr) {
        const out = [];
        let last = null;
        arr.forEach(m => {
            const d = new Date(m.timestamp).toISOString().slice(0, 10);
            if (d !== last) {
                out.push({ type: 'date', date: m.timestamp });
                last = d;
            }
            out.push({ type: 'msg', ...m });
        });
        return out;
    }
    const grouped = groupByDate(messages);
    function isMsgFromMe(m) {
        return m.senderId._id === currentUserId;
    }

    // Collect all users you might want to map from their IDs
    const allUsers = [
        profileData,
        ...contacts.map(c => c.user),
        ...(selected?.members || []), // for groups
    ];

    // Map userId -> "First Last"
    const userMap = {};
    allUsers.forEach(u => {
        if (u && u._id) {
            userMap[u._id] = `${u.firstName || ''} ${u.lastName || ''}`.trim();
        }
    });


    return (
        <Box sx={{ display: 'flex', height: '80vh', bgcolor: '#e9edf5', p: 3, gap: 2 }}>
            {/* Contact List and Groups */}
            <Box
                sx={{
                    width: 320,
                    bgcolor: '#fff',
                    borderRadius: 2,
                    boxShadow: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    pb: 1,
                    minWidth: 250,
                }}
            >
                <Stack direction="row" alignItems="center" spacing={2} sx={{ p: 2, mb: 1 }}>
                    <Avatar src={avatarUrl(profileData)} />
                    <Box>
                        <Typography fontWeight="bold">
                            {profileData.firstName} {profileData.lastName}
                        </Typography>
                        <Box display="flex" alignItems="center">
                            <StatusDot isOnline={userStatuses[profileData._id]?.isOnline} status={profileData.status} />
                            <Typography variant="caption" color="textSecondary" sx={{ mr: 0.5 }}>
                                {getStatusLabel(userStatuses[profileData._id]?.isOnline, profileData.status)}
                            </Typography>
                            <Tooltip title="Change status">
                                <IconButton size="small" onClick={e => setStatusAnchorEl(e.currentTarget)} sx={{ p: 0.5 }}>
                                    <ArrowDropDownIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <Menu anchorEl={statusAnchorEl} open={Boolean(statusAnchorEl)} onClose={() => setStatusAnchorEl(null)}>
                                {statusOptions.map(opt => (
                                    <MenuItem
                                        key={opt.value}
                                        selected={opt.value === profileData.status}
                                        onClick={async () => {
                                            await axios.put('/api/users/status', { status: opt.value }, { headers: { Authorization: `Bearer ${token}` } });
                                            setProfileData(d => ({ ...d, status: opt.value }));
                                            setStatusAnchorEl(null);
                                            socket?.emit('setStatus', { userId: profileData._id, status: opt.value });
                                        }}
                                        sx={{ color: opt.color }}
                                    >
                                        <StatusDot isOnline status={opt.value} />
                                        {opt.label}
                                    </MenuItem>
                                ))}
                            </Menu>
                        </Box>
                    </Box>
                </Stack>
                <Box sx={{ px: 2, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TextField
                        size="small"
                        fullWidth
                        placeholder="Search contacts or groups"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                            sx: { bgcolor: '#f4f4f4', borderRadius: 2 },
                        }}
                    />
                    <Tooltip title="New group chat">
                        <IconButton size="small" onClick={handleOpenGroupDialog}>
                            <GroupIcon />
                        </IconButton>
                    </Tooltip>
                </Box>

                {/* Groups */}
                <Typography sx={{ ml: 3, mt: 1, fontWeight: 600, color: '#666', fontSize: 13 }}>
                    Group Chats
                </Typography>
                <List sx={{ px: 1, pb: 0, maxHeight: 180, overflowY: 'auto' }}>
                    {filteredGroups.length === 0 && (
                        <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>No groups</Typography>
                    )}
                    {filteredGroups.map(group => {
                        const unread = group.unread || 0; // <-- now using the backend's count
                        return (
                            <ListItemButton
                                key={group._id}
                                selected={selected?.type === 'group' && selected?._id === group._id}
                                onClick={() => setSelected({ ...group, type: 'group' })}
                                sx={{ borderRadius: 2, mb: 0.5, p: 1.2 }}
                            >
                                <ListItemAvatar sx={{ minWidth: 48 }}>
                                    <AvatarGroup max={3} sx={{ ml: 0 }}>
                                        {(group.members || []).slice(0, 3).map(member =>
                                            <Avatar
                                                key={member.userId._id}
                                                src={member.userId.avatarUrl ? `/api/users/avatars/${member.userId._id}/${member.userId.avatarUrl.split('/').pop()}` : undefined}
                                                alt={`${member.userId.firstName} ${member.userId.lastName}`}
                                                sx={{ width: 32, height: 32, fontSize: 14 }}
                                            >
                                                {(!member.userId.avatarUrl && member.userId.firstName) ? member.userId.firstName[0] : ''}
                                            </Avatar>
                                        )}

                                    </AvatarGroup>
                                </ListItemAvatar>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <Typography sx={{
                                            fontWeight: 600, fontSize: 15,
                                            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                                        }}>
                                            {group.name}
                                        </Typography>
                                        {/* NEW: Add the unread badge here */}
                                        {unread > 0 && <Badge badgeContent={unread} color="error" />}
                                    </Box>
                                    <Typography variant="caption" color="textSecondary">
                                        {(group.members && group.members.length) || 0} members
                                    </Typography>
                                    {groupMessages[group._id] && (
                                        <Typography
                                            variant="body2"
                                            color="textSecondary"
                                            noWrap
                                            sx={{
                                                fontSize: 13,
                                                mt: 0.3,
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                            }}
                                        >
                                            {groupMessages[group._id].senderId?.firstName}: {groupMessages[group._id].message}
                                        </Typography>
                                    )}
                                </Box>
                            </ListItemButton>
                        );
                    })}
                </List>


                {/* 1:1 Contacts */}
                <Typography sx={{ ml: 3, mt: 2, fontWeight: 600, color: '#666', fontSize: 13 }}>
                    Direct Messages
                </Typography>
                <List sx={{ px: 1, flex: 1, overflowY: 'auto' }}>
                    {filteredContacts.map(contact => {
                        const lastMsg = contactsMessages[contact.user._id];
                        const unread = contactsUnread[contact.user._id] || 0;
                        const st = userStatuses[contact.user._id] || {};
                        return (
                            <ListItemButton
                                key={contact.user._id}
                                selected={selected?.type === 'user' && selected?.user._id === contact.user._id}
                                onClick={() => setSelected({ ...contact, type: 'user' })}
                                sx={{ alignItems: 'flex-start', borderRadius: 2, mb: 0.5, p: 1.2 }}
                            >
                                <ListItemAvatar>
                                    <Avatar src={avatarUrl(contact.user)} />
                                </ListItemAvatar>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <Typography sx={{
                                            fontWeight: 600, fontSize: 15,
                                            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                                        }}>
                                            {contact.user.firstName} {contact.user.lastName}
                                        </Typography>
                                        {unread > 0 && <Badge badgeContent={unread} color="error" />}
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <StatusDot isOnline={st.isOnline} status={st.status} />
                                        <Typography variant="caption" color="textSecondary">
                                            {getStatusLabel(st.isOnline, st.status)}
                                        </Typography>
                                    </Box>
                                    <Typography
                                        variant="body2"
                                        color="textSecondary"
                                        noWrap
                                        sx={{
                                            fontSize: 13,
                                            mt: 0.3,
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                        }}
                                    >
                                        {lastMsg
                                            ? `${isMsgFromMe(lastMsg) ? 'You: ' : ''}${lastMsg.message}`
                                            : (<span style={{ color: '#bdbdbd' }}>No messages yet</span>)}
                                    </Typography>
                                </Box>
                            </ListItemButton>
                        );
                    })}
                </List>
            </Box>

            {/* Chat Box */}
            <Box
                sx={{
                    flexGrow: 1,
                    bgcolor: '#fff',
                    borderRadius: 2,
                    boxShadow: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    minWidth: 0,
                }}
            >
                {selected ? (
                    <>
                        <Box sx={{ display: 'flex', alignItems: 'center', p: 2, pb: 1 }}>
                            {selected.type === 'group' ? (
                                <AvatarGroup max={3} sx={{ mr: 2 }}>
                                    {(selected.members || []).slice(0, 3).map(member =>
                                        <Avatar
                                            key={member.userId._id}
                                            src={member.userId.avatarUrl ? `/api/users/avatars/${member.userId._id}/${member.userId.avatarUrl.split('/').pop()}` : undefined}
                                            alt={`${member.userId.firstName} ${member.userId.lastName}`}
                                            sx={{ width: 32, height: 32, fontSize: 14 }}
                                        >
                                            {(!member.userId.avatarUrl && member.userId.firstName) ? member.userId.firstName[0] : ''}
                                        </Avatar>
                                    )}
                                </AvatarGroup>
                            ) : (
                                <Avatar src={avatarUrl(selected.user)} sx={{ mr: 2 }} />
                            )}


                            <Typography fontWeight={600} sx={{ fontSize: 18 }}>
                                {selected.type === 'user'
                                    ? `Chat with ${selected.user.firstName} ${selected.user.lastName}`
                                    : selected.name
                                }
                            </Typography>
                            {selected.type === 'group' && (
                                <>
                                    <Chip label={`${selected.members?.length || 0} members`} size="small" sx={{ ml: 2 }} />
                                    <IconButton size="small" sx={{ ml: 1 }} onClick={handleGroupMenuOpen}>
                                        <MoreVertIcon />
                                    </IconButton>
                                    <Menu
                                        anchorEl={groupMenuAnchorEl}
                                        open={Boolean(groupMenuAnchorEl)}
                                        onClose={handleGroupMenuClose}
                                    >
                                        <MenuItem onClick={handleOpenAddMembers}>
                                            Add Members
                                        </MenuItem>
                                        <MenuItem onClick={handleLeaveGroup}>Leave Group</MenuItem>
                                        {isGroupOwner && (
                                            <MenuItem onClick={() => {
                                                setDeleteGroupDialogOpen(true);
                                                handleGroupMenuClose();
                                            }} sx={{ color: 'error.main' }}>
                                                Delete Group
                                            </MenuItem>

                                        )}
                                    </Menu>
                                </>
                            )}
                            <Dialog
                                open={deleteGroupDialogOpen}
                                onClose={() => setDeleteGroupDialogOpen(false)}
                                maxWidth="xs"
                                fullWidth
                            >
                                <DialogTitle>Delete Group Chat?</DialogTitle>
                                <DialogContent>
                                    <Typography color="error" fontWeight={600} gutterBottom>
                                        This action cannot be undone.
                                    </Typography>
                                    <Typography>
                                        Are you sure you want to delete this group chat? <br />
                                        <b>All messages will be permanently deleted for all group members.</b>
                                    </Typography>
                                </DialogContent>
                                <DialogActions>
                                    <Button onClick={() => setDeleteGroupDialogOpen(false)}>
                                        Cancel
                                    </Button>
                                    <Button
                                        onClick={async () => {
                                            setDeleteGroupDialogOpen(false);
                                            await handleDeleteGroup();
                                        }}
                                        color="error"
                                        variant="contained"
                                    >
                                        Delete
                                    </Button>
                                </DialogActions>
                            </Dialog>

                        </Box>
                        <Box
                            ref={chatBoxRef}
                            sx={{
                                flexGrow: 1,
                                px: 2,
                                pb: 2,
                                pt: 0.5,
                                overflowY: 'auto',
                                background: 'transparent',
                                position: 'relative'
                            }}
                        >
                            {grouped.map((item, idx) =>
                                item.type === 'date' ? (
                                    <Divider key={idx} sx={{ my: 2 }}>
                                        <Typography sx={{ fontSize: 12, color: '#757575', fontWeight: 600 }}>
                                            {formatChatDate(item.date)}
                                        </Typography>
                                    </Divider>
                                ) : (
                                    <Box
                                        key={idx}

                                        sx={{
                                            display: 'flex',
                                            mb: 3,
                                            justifyContent:
                                                item.senderId._id === currentUserId ? 'flex-end' : 'flex-start',
                                            alignItems: 'flex-end',
                                            position: 'relative',
                                        }}
                                    >
                                        {/* Avatar (left for receiver, right for sender) */}
                                        {item.senderId._id !== currentUserId && (
                                            <Avatar src={avatarUrl(item.senderId)} sx={{ width: 32, height: 32, mr: 1 }} />
                                        )}

                                        <Box sx={{ maxWidth: '65%', position: 'relative', minWidth: 80 }}>
                                            {/* Name and time above bubble */}
                                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.3 }}>
                                                <Typography variant="caption" sx={{ fontWeight: 600, color: '#5d5d5d', mr: 0.7 }}>
                                                    {item.senderId.firstName} {item.senderId.lastName}
                                                </Typography>
                                                <Typography variant="caption" sx={{ color: '#aaa', fontSize: 11 }}>
                                                    {formatTime(item.timestamp)}
                                                </Typography>
                                            </Box>

                                            {/* Reaction bar - hugging the top of bubble */}

                                            <Box
                                                sx={{
                                                    backgroundColor: item.senderId._id === currentUserId ? '#D1E7F9' : '#F1F3F6',
                                                    color: '#222',
                                                    borderRadius: 2,
                                                    px: 1.5,
                                                    py: 1,
                                                    wordBreak: 'break-word',
                                                    fontSize: 15,
                                                    boxShadow: 'none',
                                                    position: 'relative',
                                                    minWidth: 70,
                                                }}
                                                onMouseEnter={() => setHoveredMessageId(item._id)}
                                                onMouseLeave={() => setHoveredMessageId(null)}
                                            >
                                                {/* Reaction Bar - Hugging top */}
                                                {hoveredMessageId === item._id && (
                                                    <Box
                                                        sx={{
                                                            position: 'absolute',
                                                            top: 0,
                                                            right: item.senderId._id === currentUserId ? 8 : 'auto',
                                                            left: item.senderId._id !== currentUserId ? 8 : 'auto',
                                                            transform: 'translateY(-80%)',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            bgcolor: '#fff',
                                                            borderRadius: 2,
                                                            boxShadow: '0 4px 16px 0 rgba(0,0,0,0.16)',
                                                            zIndex: 30,
                                                            px: 0.5,
                                                            py: 0.1,
                                                            minHeight: 28,
                                                            minWidth: 0,
                                                            opacity: 1,
                                                            pointerEvents: 'auto',
                                                        }}
                                                    >
                                                        {QUICK_EMOJIS.map((emojiChar, i) => {
                                                            // Check if the current user already reacted (and with what)
                                                            const myReaction = item.reactions?.find(r => r.userId === currentUserId);
                                                            const alreadyReactedWith = myReaction && myReaction.emoji === emojiChar;

                                                            return (
                                                                <IconButton
                                                                    key={i}
                                                                    size="small"
                                                                    disabled={!!myReaction && !alreadyReactedWith} // disable other emojis if already reacted
                                                                    onClick={() => {
                                                                        // If already reacted with this emoji, remove the reaction (toggle off)
                                                                        // Otherwise, add/update reaction
                                                                        if (selected.type === 'group') {
                                                                            socket.emit('addGroupReaction', {
                                                                                messageId: item._id,
                                                                                emoji: alreadyReactedWith ? null : emojiChar,
                                                                                userId: currentUserId,
                                                                                groupId: selected._id, // for group!
                                                                            });
                                                                        } else {
                                                                            socket.emit('addTeamReaction', {
                                                                                messageId: item._id,
                                                                                emoji: alreadyReactedWith ? null : emojiChar,
                                                                                userId: currentUserId,
                                                                                teamId: selected.teamId,
                                                                            });
                                                                        }
                                                                        setHoveredMessageId(null);

                                                                    }}

                                                                    sx={{
                                                                        fontSize: 16,
                                                                        mx: 0.2,
                                                                        p: 0.2,
                                                                        minWidth: 24,
                                                                        minHeight: 24,
                                                                        bgcolor: alreadyReactedWith ? '#e0f7fa' : 'transparent', // highlight selected
                                                                    }}
                                                                >
                                                                    <span style={{ fontSize: 16 }}>{emojiChar}</span>
                                                                </IconButton>
                                                            );
                                                        })}

                                                        <IconButton
                                                            size="small"
                                                            onClick={() => {
                                                                setReactionPicker({ messageId: item._id });
                                                                setHoveredMessageId(null);
                                                            }}
                                                            sx={{
                                                                mx: 0.1,
                                                                p: 0.2,
                                                                minWidth: 28,
                                                                minHeight: 28,
                                                                opacity: 1,
                                                                filter: 'none'
                                                            }}
                                                        >
                                                            <MoreVertIcon fontSize="small" />
                                                        </IconButton>
                                                    </Box>
                                                )}

                                                {/* Message Text */}
                                                {item.message}

                                                {/* Displayed Emoji(s) - bottom right */}
                                                {item.reactions && item.reactions.length > 0 && (
                                                    <Box
                                                        sx={{
                                                            position: 'absolute',
                                                            bottom: -8,
                                                            right: 8,
                                                            display: 'flex',
                                                            gap: 0.5,
                                                            zIndex: 2,
                                                            pointerEvents: 'auto',
                                                            alignItems: 'center'
                                                        }}
                                                    >
                                                        {groupReactions(item.reactions, userMap).map((reaction, i) => (
                                                            <Tooltip key={i} title={reaction.users.join(', ')} arrow placement="top">
                                                                <span
                                                                    style={{
                                                                        display: 'inline-flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                        fontSize: 16,                // Bigger or smaller as needed
                                                                        lineHeight: 1,               // Important! Prevents vertical crop
                                                                        userSelect: 'none',
                                                                        borderRadius: 16,            // Full "chip" pill
                                                                        padding: '2px 10px',         // Looks like a chip, not a square
                                                                        background: '#fff',       // Subtle chip color (NOT transparent/white)
                                                                        cursor: 'pointer',
                                                                        margin: '0 2px',
                                                                        boxShadow: 'none',           // Removes shadow
                                                                        border: '1px solid #e0e0e0', // (Optional) subtle outline
                                                                    }}
                                                                >
                                                                    {reaction.emoji}
                                                                    {reaction.users.length > 1 && (
                                                                        <span style={{
                                                                            fontSize: 13,
                                                                            marginLeft: 4,
                                                                            fontWeight: 600,
                                                                            color: '#666'
                                                                        }}>
                                                                            {reaction.users.length}
                                                                        </span>
                                                                    )}
                                                                </span>

                                                            </Tooltip>
                                                        ))}
                                                    </Box>
                                                )}
                                            </Box>


                                            {/* Emoji Picker: absolute above bubble, hugging it, with solid shadow */}
                                            {reactionPicker.messageId === item._id && (
                                                <Box
                                                    sx={{
                                                        position: 'absolute',
                                                        top: 0,
                                                        left: item.senderId._id === currentUserId ? 'auto' : 0,
                                                        right: item.senderId._id === currentUserId ? 0 : 'auto',
                                                        transform: 'translateY(-105%)',
                                                        zIndex: 100,
                                                        bgcolor: '#fff',
                                                        border: '1px solid #bcbcbc',
                                                        borderRadius: 2,
                                                        boxShadow: '0 6px 24px 0 rgba(0,0,0,0.3), 0 1.5px 3px 0 rgba(0,0,0,0.15)',
                                                        minWidth: 200,
                                                        maxWidth: 240,
                                                        overflow: 'visible'
                                                    }}
                                                    onMouseLeave={() => setReactionPicker({ messageId: null })}
                                                >
                                                    <Picker
                                                        data={data}
                                                        onEmojiSelect={emojiObj => {
                                                            if (selected.type === 'group') {
                                                                socket.emit('addGroupReaction', {
                                                                    messageId: item._id,
                                                                    emoji: emojiObj.native,
                                                                    userId: currentUserId,
                                                                    groupId: selected._id,
                                                                });
                                                            } else {
                                                                socket.emit('addTeamReaction', {
                                                                    messageId: item._id,
                                                                    emoji: emojiObj.native,
                                                                    userId: currentUserId,
                                                                    teamId: selected.teamId,
                                                                });
                                                            }
                                                            setReactionPicker({ messageId: null });
                                                        }}
                                                        theme="light"
                                                        perLine={6}
                                                        maxFrequentRows={1}
                                                        previewPosition="none"
                                                        navPosition="bottom"
                                                        style={{ width: 200 }}
                                                    />
                                                </Box>
                                            )}
                                        </Box>

                                        {/* Avatar right for my messages */}
                                        {item.senderId._id === currentUserId && (
                                            <Avatar src={avatarUrl(item.senderId)} sx={{ width: 32, height: 32, ml: 1 }} />
                                        )}
                                    </Box>
                                )
                            )}
                        </Box>






                        {selected?.type === 'user' && typingUsers[selected.teamId] && typingUsers[selected.teamId].length > 0 && (
                            <Box sx={{ px: 2, pb: 1 }}>
                                <Typography fontSize={13} color="text.secondary">
                                    {typingUsers[selected.teamId]
                                        .filter(u => u._id !== currentUserId)
                                        .map(u => `${u.firstName} ${u.lastName}`)
                                        .join(', ')
                                    }
                                    {typingUsers[selected.teamId].length === 1 ? ' is typing...' : ' are typing...'}
                                </Typography>
                            </Box>
                        )}

                        <Box sx={{ p: 2, pt: 0, position: 'relative' }}>
                            <MessageInput
                                value={newMessage}
                                onChange={val => {
                                    setNewMessage(val);
                                    handleTyping();
                                }}
                                onSend={sendMessage}
                                onEmojiToggle={() => setShowEmojiPicker(s => !s)}
                            />
                            {showEmojiPicker && (
                                <Box
                                    ref={emojiPickerRef}
                                    sx={{ position: 'absolute', bottom: 60, right: 16, zIndex: 99 }}
                                >
                                    <Picker data={data} onEmojiSelect={handleAddEmoji} theme="light" />
                                </Box>
                            )}
                        </Box>
                        {/* Add Members Dialog */}
                        <Dialog open={addMemberDialogOpen} onClose={() => setAddMemberDialogOpen(false)} maxWidth="xs" fullWidth>
                            <DialogTitle>Add Members to Group</DialogTitle>
                            <DialogContent>
                                <Typography sx={{ mt: 1, mb: 1, fontWeight: 600 }}>Select Members</Typography>
                                <Box sx={{ maxHeight: 180, overflowY: 'auto', mb: 2 }}>
                                    {(() => {
                                        const memberIds = (selected.members || []).map(m => m.userId._id);
                                        return contacts
                                            .filter(c => !memberIds.includes(c.user._id))
                                            .map(contact => (
                                                <Chip
                                                    key={contact.user._id}
                                                    label={`${contact.user.firstName} ${contact.user.lastName}`}
                                                    color={newGroupMembers.includes(contact.user._id) ? "primary" : "default"}
                                                    onClick={() => {
                                                        setNewGroupMembers(members =>
                                                            members.includes(contact.user._id)
                                                                ? members.filter(id => id !== contact.user._id)
                                                                : [...members, contact.user._id]
                                                        );
                                                    }}
                                                    sx={{ mr: 1, mb: 1, cursor: "pointer" }}
                                                />
                                            ));
                                    })()}
                                </Box>
                            </DialogContent>
                            <DialogActions>
                                <Button onClick={() => setAddMemberDialogOpen(false)}>Cancel</Button>
                                <Button
                                    onClick={handleAddMembersToGroup}
                                    disabled={newGroupMembers.length < 1}
                                    variant="contained"
                                >
                                    Add
                                </Button>
                            </DialogActions>
                        </Dialog>
                    </>
                ) : (
                    <Box
                        sx={{
                            flexGrow: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <Typography color="textSecondary">Select a contact or group to start chatting</Typography>
                    </Box>
                )}

                {/* Create Group Dialog */}
                <Dialog open={groupDialogOpen} onClose={() => setGroupDialogOpen(false)} maxWidth="xs" fullWidth>
                    <DialogTitle>Create New Group Chat</DialogTitle>
                    <DialogContent>
                        <TextField
                            label="Group Name"
                            fullWidth
                            margin="dense"
                            value={groupName}
                            onChange={e => setGroupName(e.target.value)}
                        />
                        <Typography sx={{ mt: 2, mb: 1, fontWeight: 600 }}>Select Members</Typography>
                        <Box sx={{ maxHeight: 180, overflowY: 'auto', mb: 2 }}>
                            {contacts
                                .filter(contact => contact.user._id !== currentUserId) // Don't show current user in member list
                                .map(contact => (
                                    <Chip
                                        key={contact.user._id}
                                        label={`${contact.user.firstName} ${contact.user.lastName}`}
                                        color={groupMembers.includes(contact.user._id) ? "primary" : "default"}
                                        onClick={() => {
                                            setGroupMembers(members =>
                                                members.includes(contact.user._id)
                                                    ? members.filter(id => id !== contact.user._id)
                                                    : [...members, contact.user._id]
                                            );
                                        }}
                                        sx={{ mr: 1, mb: 1, cursor: "pointer" }}
                                    />
                                ))}
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setGroupDialogOpen(false)}>Cancel</Button>
                        <Button
                            onClick={handleCreateGroup}
                            disabled={!groupName.trim() || groupMembers.length < 1}
                            variant="contained"
                        >
                            Create
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </Box>
    );
};

export default ChatPage;
